import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Settings as SettingsApi, Automations } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { CheckCircle2, AlertCircle, ExternalLink, ArrowLeft, Loader2, Copy, Bot, Zap, Globe, BookOpen, Download, Trash2, AlertTriangle, Image as ImageIcon, Search } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import ImageDropzone from './ImageDropzone';
import { SupportSettings } from './SupportSettings';
import { AirtableImport } from './AirtableImport';
import { ReleaseTextSettings } from './ReleaseTextSettings';
import { useApp } from '../context/AppContext';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { shows } = useApp();
  const [params] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [ms, setMs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    const [s, m] = await Promise.all([SettingsApi.get(), SettingsApi.msStatus()]);
    setSettings(s);
    setMs(m);
    setForm({
      hostEmail: s.hostEmail || '',
      availableHoursStart: s.availableHoursStart ?? 7,
      availableHoursEnd: s.availableHoursEnd ?? 20,
      timezone: s.timezone || 'America/Los_Angeles',
      episodeMinutes: s.episodeMinutes ?? 75,
      riversideTemplate: s.riversideTemplate || '',
      ignoreOutlookBusy: !!s.ignoreOutlookBusy,
      automationsEnabled: s.automationsEnabled !== false,
      reminderDays: (s.reminderDays || [3, 7, 14]).join(', '),
      serperApiKey: s.serperApiKey || '',
      spotifyClientId: s.spotifyClientId || '',
      autoPublishEnabled: s.autoPublishEnabled !== false,
      spotifyClientSecret: s.spotifyClientSecret || '',
      emailHeaderImage: s.emailHeaderImage || '',
      emailSalutationImage: s.emailSalutationImage || '',
      emailSignatureImage: s.emailSignatureImage || '',
      dailyBackupEnabled: !!s.dailyBackupEnabled,
      lastBackupEmailedAt: s.lastBackupEmailedAt || null,
      blockedDates: s.blockedDates || [],
      blockedWeekly: s.blockedWeekly || []
    });
  };

  const [autoRunning, setAutoRunning] = useState(false);
  const runAutomationsNow = async () => {
    setAutoRunning(true);
    try {
      const r = await Automations.runNow();
      if (r.ok) {
        const s = r.summary;
        const emails = (s.releaseReminders || 0) + (s.upcomingReminders || 0) + (s.thankYous || 0);
        const ap = s.autoPublished || 0;
        const desc = ap > 0
          ? `Sent ${emails} email${emails === 1 ? '' : 's'} · Auto-published ${ap} guest${ap === 1 ? '' : 's'}`
          : `Sent ${emails} email${emails === 1 ? '' : 's'}`;
        toast({ title: 'Automations ran', description: desc });
      } else {
        toast({ title: 'Skipped', description: r.reason });
      }
      await load();
    } catch (e) {
      toast({ title: 'Run failed' });
    } finally {
      setAutoRunning(false);
    }
  };

  const [mentionsRunning, setMentionsRunning] = useState(false);
  const runMentionsNow = async () => {
    setMentionsRunning(true);
    try {
      const r = await Automations.searchMentions();
      if (r.ok) toast({ title: 'Mentions searched', description: `${r.summary.new} new found in ${r.summary.queries} queries` });
      else toast({ title: 'Skipped', description: r.reason });
    } catch (e) { toast({ title: 'Search failed' }); }
    finally { setMentionsRunning(false); }
  };

  useEffect(() => {
    load();
    if (params.get('connected')) toast({ title: 'Outlook connected', description: 'Email & calendar are live.' });
    if (params.get('error')) toast({ title: 'Microsoft auth failed', description: params.get('error') });
  }, []); // eslint-disable-line

  const saveImage = async (field, url, label) => {
    setForm(f => ({ ...f, [field]: url }));
    try {
      await SettingsApi.update({ [field]: url });
      toast({ title: url ? `${label} saved — it will appear on every email from now on` : `${label} removed` });
      const fresh = await SettingsApi.get();
      setSettings(fresh);
    } catch (e) {
      toast({ title: `Could not save ${label.toLowerCase()}`, description: e?.response?.data?.detail || e.message });
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const next = await SettingsApi.update({
        ...form,
        availableHoursStart: Number(form.availableHoursStart),
        availableHoursEnd: Number(form.availableHoursEnd),
        episodeMinutes: Number(form.episodeMinutes),
        ignoreOutlookBusy: !!form.ignoreOutlookBusy,
        automationsEnabled: !!form.automationsEnabled,
        reminderDays: String(form.reminderDays).split(',').map(x => parseInt(x.trim(), 10)).filter(n => !isNaN(n) && n > 0),
        serperApiKey: form.serperApiKey || '',
        spotifyClientId: form.spotifyClientId || '',
        autoPublishEnabled: !!form.autoPublishEnabled,
        spotifyClientSecret: form.spotifyClientSecret || '',
        emailHeaderImage: form.emailHeaderImage || '',
        emailSalutationImage: form.emailSalutationImage || '',
        emailSignatureImage: form.emailSignatureImage || '',
        dailyBackupEnabled: !!form.dailyBackupEnabled,
        blockedDates: form.blockedDates || [],
        blockedWeekly: form.blockedWeekly || []
      });
      setSettings(next);
      toast({ title: 'Saved' });
    } catch (e) {
      toast({ title: 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const updBlock = (key, i, patch) => setForm(f => { const arr = [...(f[key] || [])]; arr[i] = { ...arr[i], ...patch }; return { ...f, [key]: arr }; });
  const rmBlock = (key, i) => setForm(f => ({ ...f, [key]: (f[key] || []).filter((_, j) => j !== i) }));
  const addBlock = (key, item) => setForm(f => ({ ...f, [key]: [...(f[key] || []), item] }));

  const disconnect = async () => {
    await SettingsApi.msDisconnect();
    toast({ title: 'Disconnected' });
    await load();
  };

  const [resetConfirm, setResetConfirm] = useState('');
  const [resetting, setResetting] = useState(false);
  const [emailingBackup, setEmailingBackup] = useState(false);
  const [autoPubRunning, setAutoPubRunning] = useState(false);
  const runAutoPublishNow = async () => {
    setAutoPubRunning(true);
    try {
      await Automations.autoPublishNow();
      toast({ title: 'Checking your RSS feed…', description: 'This runs in the background; results appear below when done.' });
      const poll = setInterval(async () => {
        try {
          const st = await Automations.autoPublishStatus();
          if (!st.running) {
            clearInterval(poll);
            const r = st.result || {};
            toast({ title: `Auto-publish check: ${r.published?.length || 0} newly published`, description: `Checked ${r.checked ?? 0} guests · ${r.linksFilled ?? 0} got platform links${r.errors?.length ? ` · ${r.errors[0]}` : ''}`, duration: 8000 });
            await load();
            setAutoPubRunning(false);
          }
        } catch (e) { /* keep polling */ }
      }, 3000);
    } catch (e) { toast({ title: 'Auto-publish check failed', description: e?.response?.data?.detail || e.message }); setAutoPubRunning(false); }
  };
  const [restoring, setRestoring] = useState(false);
  const restoreInputRef = React.useRef(null);

  const emailBackupNow = async () => {
    setEmailingBackup(true);
    try {
      const r = await SettingsApi.emailBackupNow();
      if (r.ok) {
        toast({ title: r.warning ? 'Backup emailed (images excluded)' : 'Backup emailed', description: r.warning || `Sent to ${r.sentTo} as ${r.filename} (${r.sizeMb} MB)`, duration: r.warning ? 12000 : 5000 });
      } else {
        const why = { microsoft_not_connected: 'Outlook is not connected — click "Connect Microsoft" above first.', no_host_email: 'No host email set.' }[r.reason] || r.error || r.reason || 'unknown';
        toast({ title: 'Backup failed', description: why, duration: 10000 });
      }
      await load();
    } catch (e) {
      toast({ title: 'Backup failed', description: e?.response?.data?.detail || String(e) });
    } finally {
      setEmailingBackup(false);
    }
  };

  const onRestoreFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.zip')) {
      toast({ title: 'Pick a .zip backup file' });
      return;
    }
    if (!window.confirm(`Restore from "${file.name}"? Existing records with the same id will be SKIPPED (not overwritten). To force overwrite, hold Shift while clicking Restore.`)) {
      e.target.value = '';
      return;
    }
    setRestoring(true);
    try {
      const r = await SettingsApi.restoreData(file, false);
      const restored = r.restored || {};
      const added = Object.entries(restored).filter(([k, v]) => typeof v === 'number' && v > 0).map(([k, v]) => `${k}: +${v}`).join(' · ');
      toast({ title: 'Restore complete', description: added || 'No new rows (everything already existed in DB)' });
    } catch (err) {
      toast({ title: 'Restore failed', description: err?.response?.data?.detail || String(err) });
    } finally {
      setRestoring(false);
      e.target.value = '';
    }
  };
  const performReset = async () => {
    if (resetConfirm !== 'RESET') { toast({ title: 'Type RESET to confirm' }); return; }
    setResetting(true);
    try {
      const r = await SettingsApi.resetData();
      toast({ title: 'Data cleared', description: `Removed ${Object.values(r.cleared).reduce((a, b) => a + b, 0)} records across ${Object.keys(r.cleared).length} collections` });
      setResetConfirm('');
    } catch (e) {
      toast({ title: 'Reset failed', description: e?.response?.data?.detail || String(e) });
    } finally {
      setResetting(false);
    }
  };

  if (!settings || !ms) {
    return <div className="min-h-screen grid place-items-center bg-[#FAF7F2]"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;
  }

  const bookingUrls = ['wine-talks', 'song-of-america'].map(id => `${window.location.origin}/book/${id}`);

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <div className="max-w-3xl mx-auto p-6">
        <button onClick={() => navigate('/')} className="text-[13px] text-stone-600 hover:text-stone-900 flex items-center gap-1 mb-3">
          <ArrowLeft className="w-4 h-4" /> Back to app
        </button>
        <h1 className="text-[26px] font-serif text-stone-900 mb-1">Settings</h1>
        <p className="text-[13px] text-stone-500 mb-4">Connect Microsoft, set your availability, configure booking.</p>

        <Card className="border-stone-200 mb-4">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg grid place-items-center" style={{ background: '#722F371f', color: '#722F37' }}>
              <BookOpen className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="text-[13.5px] font-medium text-stone-900">Operations Manual</div>
              <div className="text-[11.5px] text-stone-500">Step-by-step PDF: setup, prospect outreach, booking, recording, asset delivery, troubleshooting.</div>
            </div>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/ops-manual`} target="_blank" rel="noreferrer">
              <Button variant="outline"><Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF</Button>
            </a>
          </CardContent>
        </Card>

        <Card className="border-stone-200 mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-[15px] flex items-center gap-2">
              Microsoft (Outlook email + calendar)
              {ms.connected ? (
                <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white gap-1"><CheckCircle2 className="w-3 h-3" />Connected</Badge>
              ) : (
                <Badge variant="outline" className="gap-1 border-amber-300 text-amber-700"><AlertCircle className="w-3 h-3" />Not connected</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!ms.configured && (
              <div className="mb-3 p-3 rounded-md bg-amber-50 border border-amber-200 text-[12.5px] text-amber-900">
                Server is missing <code>MS_CLIENT_ID</code> / <code>MS_CLIENT_SECRET</code>. Add them to backend .env and restart, then come back here to connect.
              </div>
            )}
            {ms.connected ? (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[13px] font-medium">{ms.account?.name}</div>
                  <div className="text-[12px] text-stone-500">{ms.account?.email}</div>
                </div>
                <Button variant="outline" onClick={disconnect}>Disconnect</Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="text-[12.5px] text-stone-600">Authorize the app to send email from your inbox and write to your Outlook calendar.</div>
                <a href={SettingsApi.msLoginUrl()}>
                  <Button disabled={!ms.configured}>Connect Outlook</Button>
                </a>
              </div>
            )}
            <div className="mt-3 text-[11px] text-stone-500">
              Azure redirect URI to register: <code className="bg-stone-100 px-1.5 py-0.5 rounded">{ms.redirectUri}</code>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200 mb-4">
          <CardHeader className="pb-2"><CardTitle className="text-[15px]">Booking availability</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Host email (for calendar/email)</Label>
                <Input value={form.hostEmail} onChange={(e) => setForm(f => ({ ...f, hostEmail: e.target.value }))} />
              </div>
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Timezone</Label>
                <Input value={form.timezone} onChange={(e) => setForm(f => ({ ...f, timezone: e.target.value }))} />
              </div>
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Available from (hour 0-23)</Label>
                <Input type="number" min="0" max="23" value={form.availableHoursStart} onChange={(e) => setForm(f => ({ ...f, availableHoursStart: e.target.value }))} />
              </div>
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Available until (hour 0-23)</Label>
                <Input type="number" min="0" max="23" value={form.availableHoursEnd} onChange={(e) => setForm(f => ({ ...f, availableHoursEnd: e.target.value }))} />
              </div>
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Episode length (minutes)</Label>
                <Input type="number" min="15" max="240" value={form.episodeMinutes} onChange={(e) => setForm(f => ({ ...f, episodeMinutes: e.target.value }))} />
              </div>
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Riverside studio template URL</Label>
                <Input placeholder="https://riverside.fm/studio/..." value={form.riversideTemplate} onChange={(e) => setForm(f => ({ ...f, riversideTemplate: e.target.value }))} />
              </div>
            </div>
            <div className="text-[11px] text-stone-500">Riverside’s API does not support session creation. Paste a default studio link — it will be included in confirmation emails. You can override per guest.</div>
            <label className="flex items-center gap-2.5 p-3 rounded-md border border-stone-200 cursor-pointer hover:bg-stone-50">
              <input type="checkbox" checked={!!form.ignoreOutlookBusy} onChange={(e) => setForm(f => ({ ...f, ignoreOutlookBusy: e.target.checked }))} className="w-4 h-4 accent-stone-900" />
              <div className="flex-1">
                <div className="text-[13px] font-medium text-stone-900">Allow double-booking</div>
                <div className="text-[11.5px] text-stone-500">If checked, the booking page shows ALL time slots even when your Outlook calendar already has events at that time. Existing app bookings are still excluded.</div>
              </div>
            </label>

            <div className="pt-3 border-t border-stone-100" data-testid="blocked-times-section">
              <div className="text-[13px] font-medium text-stone-900">Blocked times</div>
              <div className="text-[11.5px] text-stone-500 mb-3">Times guests can never book — on top of your Outlook busy times. Leave the time fields empty to block the entire day.</div>

              {((form.blockedDates || []).filter(b => b.date).length + (form.blockedWeekly || []).length) > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3" data-testid="blocked-summary">
                  {(form.blockedDates || []).filter(b => b.date).map((b, i) => (
                    <span key={`sd${i}`} className="px-2 py-0.5 rounded-full bg-stone-100 border border-stone-200 text-stone-700 text-[11px]">
                      {new Date(`${b.date}T00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}{(b.startTime || b.endTime) ? ` · ${b.startTime || '00:00'}–${b.endTime || '24:00'}` : ' · all day'}
                    </span>
                  ))}
                  {(form.blockedWeekly || []).map((b, i) => (
                    <span key={`sw${i}`} className="px-2 py-0.5 rounded-full bg-stone-100 border border-stone-200 text-stone-700 text-[11px]">
                      Every {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][b.weekday ?? 0]}{(b.startTime || b.endTime) ? ` · ${b.startTime || '00:00'}–${b.endTime || '24:00'}` : ' · all day'}
                    </span>
                  ))}
                </div>
              )}

              <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5">Specific dates</div>
              {(form.blockedDates || []).map((b, i) => (
                <div key={`bd-${i}`} data-testid={`blocked-date-row-${i}`} className="flex items-center gap-2 mb-1.5">
                  <Input type="date" value={b.date || ''} onChange={(e) => updBlock('blockedDates', i, { date: e.target.value })} className="w-40 text-[13px]" data-testid={`blocked-date-input-${i}`} />
                  <Input type="time" value={b.startTime || ''} onChange={(e) => updBlock('blockedDates', i, { startTime: e.target.value })} className="w-28 text-[13px]" data-testid={`blocked-date-start-${i}`} />
                  <span className="text-stone-400 text-[12px]">to</span>
                  <Input type="time" value={b.endTime || ''} onChange={(e) => updBlock('blockedDates', i, { endTime: e.target.value })} className="w-28 text-[13px]" data-testid={`blocked-date-end-${i}`} />
                  <span className="text-[11px] text-stone-400 flex-1">{!b.startTime && !b.endTime ? 'whole day' : ''}</span>
                  <button type="button" data-testid={`blocked-date-remove-${i}`} onClick={() => rmBlock('blockedDates', i)} className="p-1.5 rounded hover:bg-red-50 text-stone-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <Button variant="outline" size="sm" data-testid="add-blocked-date-btn" onClick={() => addBlock('blockedDates', { date: '', startTime: '', endTime: '' })} className="mb-4 text-[12px]">+ Block a date</Button>

              <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5">Every week</div>
              {(form.blockedWeekly || []).map((b, i) => (
                <div key={`bw-${i}`} data-testid={`blocked-weekly-row-${i}`} className="flex items-center gap-2 mb-1.5">
                  <select
                    value={b.weekday ?? 0}
                    onChange={(e) => updBlock('blockedWeekly', i, { weekday: Number(e.target.value) })}
                    data-testid={`blocked-weekly-day-${i}`}
                    className="h-10 w-40 rounded-md border border-stone-200 bg-white px-2 text-[13px]"
                  >
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((d, di) => <option key={d} value={di}>Every {d}</option>)}
                  </select>
                  <Input type="time" value={b.startTime || ''} onChange={(e) => updBlock('blockedWeekly', i, { startTime: e.target.value })} className="w-28 text-[13px]" data-testid={`blocked-weekly-start-${i}`} />
                  <span className="text-stone-400 text-[12px]">to</span>
                  <Input type="time" value={b.endTime || ''} onChange={(e) => updBlock('blockedWeekly', i, { endTime: e.target.value })} className="w-28 text-[13px]" data-testid={`blocked-weekly-end-${i}`} />
                  <span className="text-[11px] text-stone-400 flex-1">{!b.startTime && !b.endTime ? 'whole day' : ''}</span>
                  <button type="button" data-testid={`blocked-weekly-remove-${i}`} onClick={() => rmBlock('blockedWeekly', i)} className="p-1.5 rounded hover:bg-red-50 text-stone-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <Button variant="outline" size="sm" data-testid="add-blocked-weekly-btn" onClick={() => addBlock('blockedWeekly', { weekday: 0, startTime: '', endTime: '' })} className="text-[12px]">+ Block a weekly slot</Button>
            </div>

            <div className="flex justify-end"><Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</Button></div>
          </CardContent>
        </Card>

        <Card className="border-stone-200 mb-4" data-testid="email-header-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-[15px] flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-stone-500" /> Email header graphic
              {form.emailHeaderImage ? (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>
              ) : (
                <Badge variant="outline">Plain text</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-[12px] text-stone-600">
              Upload a banner image (logo / show artwork) that will be prepended at the top of every outbound email — guest sends, reminders, thank-yous, bulk emails, and prospect outreach. <b>For maximum sharpness:</b> upload at 2× resolution (recommended 1200×300 px). Keep file size under 500 KB so it doesn{'\u2019'}t slow inbox loads.
            </div>
            <div className="max-w-2xl">
              <ImageDropzone
                value={form.emailHeaderImage}
                onChange={(url) => saveImage('emailHeaderImage', url, 'Header banner')}
                label="Drop banner image or click to upload (1200×300 recommended)"
                aspect="banner"
              />
            </div>
            {form.emailHeaderImage && (
              <div className="text-[11px] text-stone-500">
                Image URL: <a className="text-stone-700 underline break-all" href={form.emailHeaderImage} target="_blank" rel="noreferrer">{form.emailHeaderImage}</a>
                <Button
                  data-testid="clear-header-image-btn"
                  variant="ghost"
                  size="sm"
                  className="ml-2 h-6 text-red-600"
                  onClick={() => saveImage('emailHeaderImage', '', 'Header banner')}
                >
                  Remove
                </Button>
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} size="sm">
                {saving ? 'Saving…' : 'Save header'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200 mb-4" data-testid="email-salutation-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-[15px] flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-stone-500" /> Email salutation graphic
              {form.emailSalutationImage ? (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>
              ) : (
                <Badge variant="outline">Empty</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-[12px] text-stone-600">
              Optional. A second graphic shown <i>after</i> the header and <i>before</i> the email text — e.g. a signature handwritten {'"\u201CHi there!\u201D"'}, a wax-stamp logo, or a small show mark. Recommended: 600×200 px (transparent PNG works best). Upload at 2× (1200×400) for retina sharpness.
            </div>
            <div className="max-w-2xl">
              <ImageDropzone
                value={form.emailSalutationImage}
                onChange={(url) => saveImage('emailSalutationImage', url, 'Salutation graphic')}
                label="Drop salutation graphic or click to upload"
                aspect="banner"
              />
            </div>
            {form.emailSalutationImage && (
              <div className="text-[11px] text-stone-500">
                Image URL: <a className="text-stone-700 underline break-all" href={form.emailSalutationImage} target="_blank" rel="noreferrer">{form.emailSalutationImage}</a>
                <Button
                  data-testid="clear-salutation-image-btn"
                  variant="ghost"
                  size="sm"
                  className="ml-2 h-6 text-red-600"
                  onClick={() => saveImage('emailSalutationImage', '', 'Salutation graphic')}
                >
                  Remove
                </Button>
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} size="sm">
                {saving ? 'Saving…' : 'Save salutation'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200 mb-4" data-testid="email-signature-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-[15px] flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-stone-500" /> Email signature graphic
              {form.emailSignatureImage ? (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>
              ) : (
                <Badge variant="outline">Empty</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-[12px] text-stone-600">
              Your signature, appended at the <i>bottom</i> of every outbound email. Replaces the old plain-text {'"\u201CThanks, Paul\u201D"'} closings — the 12 email templates have already been stripped of those lines. Upload a scanned signature, a logo, or a styled name graphic. Recommended: 720×200 px PNG with transparent background. Displayed at up to 360px in emails so a 2× source keeps retina sharpness.
            </div>
            <div className="max-w-2xl">
              <ImageDropzone
                value={form.emailSignatureImage}
                onChange={(url) => saveImage('emailSignatureImage', url, 'Signature graphic')}
                label="Drop signature graphic or click to upload"
                aspect="banner"
              />
            </div>
            {form.emailSignatureImage && (
              <div className="text-[11px] text-stone-500">
                Image URL: <a className="text-stone-700 underline break-all" href={form.emailSignatureImage} target="_blank" rel="noreferrer">{form.emailSignatureImage}</a>
                <Button
                  data-testid="clear-signature-image-btn"
                  variant="ghost"
                  size="sm"
                  className="ml-2 h-6 text-red-600"
                  onClick={() => saveImage('emailSignatureImage', '', 'Signature graphic')}
                >
                  Remove
                </Button>
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} size="sm">
                {saving ? 'Saving…' : 'Save signature'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200 mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-[15px] flex items-center gap-2">
              <Bot className="w-4 h-4 text-stone-500" /> Automations
              {form.automationsEnabled !== false ? (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Daily 9am PT</Badge>
              ) : (
                <Badge variant="outline">Disabled</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-[12px] text-stone-600">Automatically handles: release-form reminders, 24-hour upcoming-show reminders, 24-hour post-show thank-you emails, and <b>auto-publish</b> (flips status to {'\u201C'}Published{'\u201D'} the next time it runs after you paste a Spotify/Apple/YouTube link on a recorded or edited guest).</div>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" checked={!!form.automationsEnabled} onChange={(e) => setForm(f => ({ ...f, automationsEnabled: e.target.checked }))} className="w-4 h-4 accent-stone-900" />
              <span className="text-[13px] text-stone-800">Run daily at 9am PT</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Reminder cadence (days, comma-separated)</Label>
                <Input value={form.reminderDays} onChange={(e) => setForm(f => ({ ...f, reminderDays: e.target.value }))} placeholder="3, 7, 14" />
              </div>
              <div className="flex items-end">
                <Button onClick={runAutomationsNow} disabled={autoRunning} variant="outline" className="w-full">
                  <Zap className="w-3.5 h-3.5 mr-1.5" /> {autoRunning ? 'Running…' : 'Run now'}
                </Button>
              </div>
            </div>
            {settings.lastAutomationRunAt && <div className="text-[11px] text-stone-500">Last run: {new Date(settings.lastAutomationRunAt).toLocaleString()}</div>}
          </CardContent>
        </Card>

        <Card className="border-stone-200 mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-[15px] flex items-center gap-2">
              <Globe className="w-4 h-4 text-stone-500" /> Web monitoring (mentions)
              {form.serperApiKey ? (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Weekly Mon 8am PT</Badge>
              ) : (
                <Badge variant="outline" className="border-amber-300 text-amber-700">No API key</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-[12px] text-stone-600">Searches Google weekly for each published guest mentioning your show name. Results appear under the <strong>Mentions</strong> tab.</div>
            <div>
              <Label className="text-[11px] uppercase text-stone-500">Serper.dev API key</Label>
              <Input type="password" placeholder="Paste your Serper API key" value={form.serperApiKey} onChange={(e) => setForm(f => ({ ...f, serperApiKey: e.target.value }))} />
              <div className="text-[11px] text-stone-500 mt-1">Get one free at <a href="https://serper.dev" target="_blank" rel="noreferrer" className="underline">serper.dev</a> (2500 free queries).</div>
            </div>
            <Button onClick={runMentionsNow} disabled={mentionsRunning || !form.serperApiKey} variant="outline">
              <Globe className="w-3.5 h-3.5 mr-1.5" /> {mentionsRunning ? 'Searching…' : 'Search mentions now'}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-stone-200 mb-4" data-testid="auto-publish-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-[15px] flex items-center gap-2">
              <Zap className="w-4 h-4 text-stone-500" /> Auto-publish detection
              {form.autoPublishEnabled ? (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Nightly 7am PT</Badge>
              ) : (
                <Badge variant="outline">Off</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-[12px] text-stone-600">
              Every night the app reads your show's RSS feed. When a <i>Recorded</i> or <i>In Editing</i> guest's episode appears (matched by episode title or guest name), the guest flips to <b>Published</b>, the publish date and title are filled in, and Apple / Spotify / YouTube / website links are grabbed automatically.
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-[12.5px] cursor-pointer">
                <input type="checkbox" data-testid="auto-publish-toggle" checked={!!form.autoPublishEnabled} onChange={(e) => setForm(f => ({ ...f, autoPublishEnabled: e.target.checked }))} />
                Run the check automatically each night
              </label>
              <Button data-testid="auto-publish-run-btn" onClick={runAutoPublishNow} disabled={autoPubRunning} variant="outline" size="sm">
                <Zap className="w-3.5 h-3.5 mr-1.5" /> {autoPubRunning ? 'Checking feed…' : 'Check now'}
              </Button>
            </div>
            {settings.lastAutoPublishRunAt && (
              <div className="text-[11.5px] text-stone-600 bg-stone-50 border border-stone-200 rounded px-3 py-2" data-testid="auto-publish-last-run">
                Last run {new Date(settings.lastAutoPublishRunAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })} · checked {settings.lastAutoPublishResult?.checked ?? 0} · published {settings.lastAutoPublishResult?.published?.length ?? 0} · links filled {settings.lastAutoPublishResult?.linksFilled ?? 0}
                {(settings.lastAutoPublishResult?.published || []).slice(0, 5).map((p, i) => <div key={i} className="text-stone-500">→ {p.name}: “{p.title}” ({p.date})</div>)}
                {(settings.lastAutoPublishResult?.errors || []).slice(0, 2).map((e, i) => <div key={i} className="text-amber-700">⚠ {e}</div>)}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-stone-200 mb-4" data-testid="platform-links-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-[15px] flex items-center gap-2">
              <Search className="w-4 h-4 text-stone-500" /> Platform link auto-find
              {form.spotifyClientId && form.spotifyClientSecret ? (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Spotify connected</Badge>
              ) : (
                <Badge variant="outline" className="border-amber-300 text-amber-700">Spotify via Google only</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-[12px] text-stone-600">
              Auto-find matches each guest's episode title (or name) directly against <b>Apple Podcasts</b>, your <b>RSS feed</b> (website + YouTube links in show notes) and <b>Spotify</b> — no waiting for Google to index. Google (Serper) is only used as a fallback. Spotify needs free developer credentials from <a href="https://developer.spotify.com/dashboard" target="_blank" rel="noreferrer" className="underline">developer.spotify.com</a>.
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Spotify Client ID</Label>
                <Input data-testid="spotify-client-id-input" placeholder="32-character client id" value={form.spotifyClientId} onChange={(e) => setForm(f => ({ ...f, spotifyClientId: e.target.value.trim() }))} className="font-mono" />
              </div>
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Spotify Client Secret</Label>
                <Input data-testid="spotify-client-secret-input" type="password" placeholder="Client secret" value={form.spotifyClientSecret} onChange={(e) => setForm(f => ({ ...f, spotifyClientSecret: e.target.value.trim() }))} className="font-mono" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200 mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-[15px] flex items-center gap-2">
              <Zap className="w-4 h-4 text-stone-500" /> Riverside via Zapier
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-[12px] text-stone-600">
              Riverside&apos;s direct API is read-only, but their <strong>Zapier integration</strong> can push session-completed events to your app. When a Zap fires this webhook, the matching guest&apos;s recording link is updated and status flips to &quot;recorded&quot;.
            </div>
            <div>
              <Label className="text-[11px] uppercase text-stone-500">Webhook URL (paste into Zapier &quot;Webhooks by Zapier&quot; action)</Label>
              <div className="flex items-center gap-2 p-2 rounded border border-stone-200 bg-stone-50">
                <code className="flex-1 text-[11.5px] text-stone-700 truncate">{`${window.location.origin}/api/webhooks/zapier/recording`}</code>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/api/webhooks/zapier/recording`); toast({ title: 'Copied' }); }}><Copy className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            <div className="text-[11px] text-stone-500 leading-relaxed">
              <strong>How to set it up in Zapier:</strong>
              <ol className="list-decimal list-inside space-y-0.5 mt-1">
                <li>Create a new Zap. Trigger: <strong>Riverside</strong> &rarr; <strong>New Recording Completed</strong>.</li>
                <li>Action: <strong>Webhooks by Zapier</strong> &rarr; <strong>POST</strong>.</li>
                <li>URL: paste the URL above.</li>
                <li>Payload type: <code>JSON</code>. Map fields: <code>email</code>, <code>name</code>, <code>recording_url</code>, <code>studio_url</code>, <code>session_id</code>, <code>completed_at</code>.</li>
                <li>Test it. The guest matching by email will have recording link + status updated.</li>
              </ol>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200">
          <CardHeader className="pb-2"><CardTitle className="text-[15px]">Public booking links</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {bookingUrls.map(u => (
              <div key={u} className="flex items-center gap-2 p-2 rounded border border-stone-200">
                <code className="flex-1 text-[12px] text-stone-700 truncate">{u}</code>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(u); toast({ title: 'Copied' }); }}><Copy className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => window.open(u, '_blank')}><ExternalLink className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
            <div className="text-[11.5px] text-stone-500">Share these instead of Calendly. Guests book a slot → the app creates an Outlook event + sends confirmation with the release form link.</div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200 mt-4" data-testid="daily-backup-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-[15px] flex items-center gap-2 text-emerald-800">
              <Download className="w-4 h-4" /> Daily email backup
              {form.dailyBackupEnabled ? (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Enabled</Badge>
              ) : (
                <Badge variant="outline">Off</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-[12.5px] text-stone-700">
              Every morning at 6:00 AM UTC, the system emails a full <code>.zip</code> backup (JSON + CSV for every collection) to your host email <b>{form.hostEmail || '(set hostEmail above first)'}</b>. Keep the emails in your Outlook for permanent off-site storage — they survive any DB issue. Restores in 60 seconds via the card below.
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-[12.5px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.dailyBackupEnabled}
                  onChange={(e) => setForm(f => ({ ...f, dailyBackupEnabled: e.target.checked }))}
                  data-testid="daily-backup-toggle"
                />
                Send the backup automatically each day
              </label>
              <Button onClick={save} disabled={saving} size="sm" variant="outline">
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
            <div className="flex items-center justify-between gap-2 pt-1">
              {(() => {
                const on = !!settings?.dailyBackupEnabled;
                const last = form.lastBackupEmailedAt ? new Date(form.lastBackupEmailedAt) : null;
                const stale = on && (!last || (Date.now() - last.getTime()) > 36 * 3600 * 1000);
                const err = settings?.lastBackupError;
                const cls = !on && !err ? 'bg-stone-100 text-stone-600 border-stone-200' : (stale || err) ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200';
                return (
                  <div data-testid="daily-backup-status" className={`text-[11.5px] font-medium border rounded px-2.5 py-1 ${cls}`}>
                    Daily backup: <b>{on ? 'ON' : 'OFF'}</b>
                    {' · '}last sent: <b>{last ? last.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'never'}</b>
                    {settings?.lastBackupSizeMb ? <span className="font-normal"> ({settings.lastBackupSizeMb} MB)</span> : null}
                    {err ? <div className="font-normal mt-0.5" data-testid="daily-backup-error">{last && settings.lastBackupAttemptAt && new Date(settings.lastBackupAttemptAt) <= new Date(last.getTime() + 1000) ? 'Note: ' : `Last attempt${settings.lastBackupAttemptAt ? ` ${new Date(settings.lastBackupAttemptAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}` : ''} failed: `}{err}</div>
                      : stale && <span className="ml-1">— no backup in the last 36h, check your Outlook connection</span>}
                    {on !== !!form.dailyBackupEnabled && <span className="ml-1 text-stone-500 font-normal">(unsaved change)</span>}
                  </div>
                );
              })()}
              <Button data-testid="email-backup-now-btn" onClick={emailBackupNow} disabled={emailingBackup} size="sm">
                <Download className="w-3.5 h-3.5 mr-1.5" /> {emailingBackup ? 'Sending…' : 'Send backup now'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200 mt-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-[15px] flex items-center gap-2">
              <Download className="w-4 h-4" /> Export all data
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-[12.5px] text-stone-700">
              Download a ZIP backup with <b>JSON + CSV</b> for every collection (guests, prospects, mentions, release submissions, templates, files, webhook history, settings). Microsoft OAuth tokens are stripped for safety.
            </div>
            <div className="flex justify-end">
              <Button asChild data-testid="export-data-btn" variant="outline">
                <a href={SettingsApi.exportDataUrl()} download>
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Download backup (.zip)
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-200 mt-4" data-testid="restore-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-[15px] flex items-center gap-2 text-blue-800">
              <Download className="w-4 h-4 rotate-180" /> Restore from backup
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-[12.5px] text-stone-700">
              Upload a previously-exported <code>.zip</code> (from the Export card above, or from your daily backup email). The system <b>upserts by id</b> — rows that already exist are skipped, only missing rows are added. Microsoft connection is preserved.
            </div>
            <input
              type="file"
              accept=".zip,application/zip"
              ref={restoreInputRef}
              onChange={onRestoreFile}
              className="hidden"
              data-testid="restore-file-input"
            />
            <div className="flex justify-end">
              <Button data-testid="restore-btn" onClick={() => restoreInputRef.current?.click()} disabled={restoring} variant="outline">
                {restoring ? 'Restoring…' : 'Choose .zip and restore'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <ReleaseTextSettings settings={settings} shows={shows} onSaved={load} />

        <SupportSettings settings={settings} onSaved={load} />

        <AirtableImport />

        <Card className="border-red-200 mt-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-[15px] flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-4 h-4" /> Danger zone — clear all data
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-[12.5px] text-stone-700">
              Permanently delete <b>all guests, prospects, mentions, uploaded files, release submissions, and webhook history</b> in this environment. <b>Email templates, settings, and your Microsoft connection are preserved.</b>
            </div>
            <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              ⚠️ This action cannot be undone. Use this when you finish testing and want a clean slate before sharing the app with real guests.
            </div>
            <div>
              <Label className="text-[11px] uppercase text-stone-500">Type <code className="bg-stone-100 px-1 rounded">RESET</code> below to enable the button</Label>
              <Input value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} placeholder='Type RESET' className="font-mono" />
            </div>
            <div className="flex justify-end">
              <Button onClick={performReset} disabled={resetConfirm !== 'RESET' || resetting} className="bg-red-600 hover:bg-red-700 text-white">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> {resetting ? 'Clearing…' : 'Clear all data'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
